import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import styled from "styled-components";
import { theme } from "styles/theme";
import { useAdminCreateTemporaryDealerAccess } from "hooks/useTemporaryDealerAccess";
import {
  AdminTemporaryAccessMode,
  AdminTemporaryAccessResult,
} from "types/temporaryAccess.types";
import { normalizeLifeguardSerial } from "utils/lifeGuardSerial";
import { notify } from "utils/notify";
import { FormField } from "components/shared/FormField";
import { FormLabel } from "components/shared/FormLabel";
import {
  FormError,
  getFieldAriaProps,
  fieldErrorId,
} from "components/shared/FormError";

const Description = styled.p`
  line-height: 1.5;
  margin-top: 0;
`;

const HelpText = styled.p`
  font-size: 0.9rem;
  color: ${theme.colors.textSecondary};
  margin-top: 0.25rem;
`;

const schema = yup.object({
  serialNumber: yup
    .string()
    .trim()
    .required("Device serial number is required"),
});

type FormData = yup.InferType<typeof schema>;

const FIELD_ID = "adminTemporaryAccessSerial";

const MODE_COPY: Record<
  AdminTemporaryAccessMode,
  {
    header: (dealerName: string) => string;
    description: string;
    submitLabel: string;
    submitIcon: string;
    errorFallback: string;
  }
> = {
  grant: {
    header: (dealerName) => `Grant Temporary Access to ${dealerName}`,
    description:
      "The dealer gets diagnostic access to this Lifeguard right away, for 72 hours. The customer is not asked to approve and no email is sent. A pending request from this dealer for the same device is replaced.",
    submitLabel: "Grant Access",
    submitIcon: "pi pi-check",
    errorFallback: "Failed to grant temporary access.",
  },
  request: {
    header: (dealerName) => `Request Temporary Access for ${dealerName}`,
    description:
      "The device owner receives an email asking them to authorise 72 hours of diagnostic access for this dealer. The request expires after 7 days without a response.",
    submitLabel: "Send Request",
    submitIcon: "pi pi-send",
    errorFallback: "Failed to send the temporary access request.",
  },
};

const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";

const notifySuccess = (
  result: AdminTemporaryAccessResult,
  dealerName: string,
) => {
  if (result.mode === "grant") {
    notify.success(
      `${dealerName} has temporary access to ${result.serialNumber} until ${formatDateTime(result.accessExpiresAt)}.`,
    );
    return;
  }
  if (result.emailsSent === 0) {
    notify.warning(
      `The request for ${result.serialNumber} was created, but the email to the customer could not be delivered.`,
    );
    return;
  }
  notify.success(
    `Temporary access request for ${result.serialNumber} was emailed to the customer and is awaiting their approval.`,
  );
};

interface AdminTemporaryAccessDialogProps {
  visible: boolean;
  mode: AdminTemporaryAccessMode;
  dealerId: number;
  dealerName: string;
  onHide: () => void;
}

/**
 * Admin-only dialog for Dealer Details > Linked Devices.
 * "grant" gives the dealer 72h access now; "request" emails the customer.
 */
const AdminTemporaryAccessDialog: React.FC<
  AdminTemporaryAccessDialogProps
> = ({ visible, mode, dealerId, dealerName, onHide }) => {
  const createAccess = useAdminCreateTemporaryDealerAccess();
  const isPending = createAccess.isPending;
  const copy = MODE_COPY[mode];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { serialNumber: "" },
  });

  const handleCancel = () => {
    reset({ serialNumber: "" });
    onHide();
  };

  const onSubmit = (data: FormData) => {
    createAccess.mutate(
      {
        dealerId,
        serialNumber: normalizeLifeguardSerial(data.serialNumber),
        mode,
      },
      {
        onSuccess: (result) => {
          notifySuccess(result, dealerName);
          reset({ serialNumber: "" });
          onHide();
        },
        onError: (error: any) => {
          notify.error(error?.response?.data?.message || copy.errorFallback);
        },
      },
    );
  };

  const footerContent = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleCancel}
        className="p-button-text"
        disabled={isPending}
      />
      <Button
        label={copy.submitLabel}
        icon={copy.submitIcon}
        onClick={handleSubmit(onSubmit)}
        loading={isPending}
      />
    </div>
  );

  return (
    <Dialog
      header={copy.header(dealerName)}
      visible={visible}
      style={{ width: "min(32rem, 92vw)" }}
      onHide={handleCancel}
      footer={footerContent}
      closable={!isPending}
      closeOnEscape={!isPending}
      draggable={false}
    >
      <form className="p-fluid" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Description>{copy.description}</Description>
        <FormField>
          <FormLabel htmlFor={FIELD_ID}>Device Serial Number</FormLabel>
          <InputText
            placeholder="e.g. 12345678 or DWLG-12345678"
            autoComplete="off"
            {...register("serialNumber")}
            {...getFieldAriaProps(FIELD_ID, errors.serialNumber?.message)}
            className={errors.serialNumber ? "p-invalid" : ""}
            disabled={isPending}
            autoFocus
          />
          <FormError id={fieldErrorId(FIELD_ID)}>
            {errors.serialNumber?.message}
          </FormError>
          <HelpText>
            Enter the device number or its full serial number.
          </HelpText>
        </FormField>
      </form>
    </Dialog>
  );
};

export default AdminTemporaryAccessDialog;
